// Single-relation parser used by `aliasScope.ts`. Reads one
// `<name>[(.<name>)*] [(<args>)] [[AS] <alias>]` token sequence and returns
// the structured relation reference. Splits TVF args (`generate_series(1,2)`)
// from regular table refs by checking for a `(` immediately after the
// qualified-name part.

import type { Token } from '../context/tokenizer';

import { parseIdentifierToken } from './identifierUtils';

export type RelationParse = {
  readonly alias: string;
  readonly relation: string;
  readonly schema?: string;
  readonly database?: string;
  readonly kind: 'table' | 'derived';
  readonly lastConsumedIdx: number;
  /**
   * True when a distinct alias token was consumed (`… AS o` / `… o`);
   * false when the relation self-aliases to its last name part
   * (`FROM sales.orders` → alias `orders`).
   */
  readonly explicitAlias: boolean;
  /**
   * The alias's VERBATIM source spelling INCLUDING quotes (`"Ord"`,
   * `[o]`, `` `o` ``). For a self-aliased relation, the last name
   * part's verbatim spelling.
   */
  readonly aliasSql: string;
  /**
   * PostgreSQL table-function column-list form — `generate_series(1, 10)
   * AS g(n)`. Populated only when the FROM-entry is a derived TVF and
   * the writer attached a `(col_list)` to the alias. Empty / undefined
   * for plain tables and ordinary subqueries.
   */
  readonly syntheticColumns?: readonly string[];
};

export function parseRelationName(
  tokens: readonly Token[],
  startIdx: number,
  introducer?: string,
): RelationParse | null {
  let i = startIdx;
  const head = tokens[i];
  if (!head || head.type !== 'ident') return null;

  const parts: string[] = [parseIdentifierToken(head.text).content];
  // Verbatim source spelling (incl. quotes) of the last name part — the
  // self-alias `aliasSql` when no explicit alias token follows (T6a).
  let lastPartSql = head.text;
  i += 1;
  while (i < tokens.length && tokens[i].type === 'dot') {
    const next = tokens[i + 1];
    if (!next || (next.type !== 'ident' && next.type !== 'kw')) {
      // Dangling dot — `FROM x.|` with the cursor right after the dot. The
      // qualified relation reference is incomplete, so don't bind a phantom
      // alias for the head identifier — that would shadow the schema during
      // dotted-access completion and the user gets no schema-table list.
      return null;
    }
    parts.push(parseIdentifierToken(next.text).content);
    lastPartSql = next.text;
    i += 2;
  }

  let kind: 'table' | 'derived' = 'table';
  // For FROM/JOIN/APPLY/LATERAL the trailing `(` is TVF args. For INTO /
  // UPDATE the trailing `(` is the column list (INSERT) or absent
  // (UPDATE). The introducer hint disables TVF consumption in those cases
  // so the binding stays kind='table' and the resolver can surface columns
  // for the bound target.
  const allowsTvf = introducer !== 'INTO' && introducer !== 'UPDATE';
  let jsonTableColumns: readonly string[] | undefined;
  if (allowsTvf && i < tokens.length && tokens[i].type === 'lparen') {
    const argsLparenIdx = i;
    const matching = findMatchingRparen(tokens, i);
    if (matching === -1) return null;
    kind = 'derived';
    // `JSON_TABLE(<doc>, '<path>' COLUMNS (col TYPE PATH '…', …)) AS t` — the
    // relation's projected columns come from the COLUMNS clause, not from a
    // SELECT body. Capture them so dotted-access (`t.col`) can surface them.
    if (parts[parts.length - 1].toUpperCase() === 'JSON_TABLE') {
      const cols = parseJsonTableColumns(tokens, argsLparenIdx, matching);
      if (cols.length > 0) jsonTableColumns = cols;
    }
    // `GRAPH_TABLE(<graph> MATCH <pattern> COLUMNS (col [AS] name, …)) AS gt`
    // (SQL/PGQ, Oracle 23ai+/PG19+) — same COLUMNS-clause-projects-columns
    // shape as JSON_TABLE above, but the column form is `expr [AS] name`
    // (trailing name) rather than JSON_TABLE's `name TYPE PATH '…'` (leading
    // name). Captured into the same `jsonTableColumns` precedence slot so
    // the explicit `AS gt(cols)` outer-alias form (parsed below) still wins.
    if (parts[parts.length - 1].toUpperCase() === 'GRAPH_TABLE') {
      const cols = parseGraphTableColumns(tokens, argsLparenIdx, matching);
      if (cols.length > 0) jsonTableColumns = cols;
    }
    i = matching + 1;
  }

  let alias = parts[parts.length - 1];
  // Provenance (T6a): unaliased relations self-alias to the last name part
  // (explicitAlias false); a consumed alias token flips both fields.
  let explicitAlias = false;
  let aliasSql = lastPartSql;
  let lastConsumedIdx = i - 1;
  if (i < tokens.length && tokens[i].type === 'kw' && tokens[i].text.toUpperCase() === 'AS') {
    if (tokens[i + 1] && tokens[i + 1].type === 'ident') {
      alias = parseIdentifierToken(tokens[i + 1].text).content;
      explicitAlias = true;
      aliasSql = tokens[i + 1].text;
      lastConsumedIdx = i + 1;
    } else {
      lastConsumedIdx = i;
    }
  } else if (i < tokens.length && tokens[i].type === 'ident') {
    alias = parseIdentifierToken(tokens[i].text).content;
    explicitAlias = true;
    aliasSql = tokens[i].text;
    lastConsumedIdx = i;
  }

  // PostgreSQL synthetic-relation column-list form — `…AS g(n)` or
  // `…<func>(...) g(a, b)` (implicit-alias variant). When the FROM-entry
  // is a TVF (`kind === 'derived'`) and a parenthesised identifier list
  // follows the alias token, capture the names so the resolver can
  // surface them as the relation's projected columns. Other engines
  // never reach this branch (only PG's standard FROM grammar accepts
  // the column-list form), so it stays a safe no-op cross-engine.
  let syntheticColumns: readonly string[] | undefined;
  if (
    kind === 'derived' &&
    lastConsumedIdx > i - 1 &&
    tokens[lastConsumedIdx + 1] &&
    tokens[lastConsumedIdx + 1].type === 'lparen'
  ) {
    const colListLparenIdx = lastConsumedIdx + 1;
    const colListRparenIdx = findMatchingRparen(tokens, colListLparenIdx);
    if (colListRparenIdx !== -1) {
      const cols = parseSyntheticColumnList(tokens, colListLparenIdx, colListRparenIdx);
      if (cols.length > 0) {
        syntheticColumns = cols;
        lastConsumedIdx = colListRparenIdx;
      }
    }
  }
  // JSON_TABLE COLUMNS clause (captured above) supplies the synthetic columns
  // when the writer didn't attach an explicit `(col_list)` to the alias.
  if (!syntheticColumns && jsonTableColumns) {
    syntheticColumns = jsonTableColumns;
  }

  return {
    alias,
    relation: parts[parts.length - 1],
    schema: parts.length >= 2 ? parts[parts.length - 2] : undefined,
    database: parts.length >= 3 ? parts[parts.length - 3] : undefined,
    kind,
    lastConsumedIdx,
    explicitAlias,
    aliasSql,
    ...(syntheticColumns ? { syntheticColumns } : {}),
  };
}

/**
 * Parse a parenthesised, comma-separated identifier list of the form
 * `(<ident> [, <ident>]…)`. Used for the PG table-function alias
 * column-list (`AS g(n, m)`). Returns an empty array when the content
 * is not a pure identifier list (e.g. contains expressions like
 * `g(1 + 2)`), which prevents us from misinterpreting expression
 * parens as column lists.
 */
function parseSyntheticColumnList(
  tokens: readonly Token[],
  lparenIdx: number,
  rparenIdx: number,
): readonly string[] {
  const cols: string[] = [];
  let expectingIdent = true;
  for (let j = lparenIdx + 1; j < rparenIdx; j += 1) {
    const t = tokens[j];
    if (expectingIdent) {
      // Treat keyword tokens as identifiers too — column names like
      // `count`, `value`, `name` may lex as `kw` depending on engine
      // tokenizer config.
      if (t.type !== 'ident' && t.type !== 'kw') return [];
      cols.push(parseIdentifierToken(t.text).content);
      expectingIdent = false;
    } else {
      if (t.type !== 'comma') return [];
      expectingIdent = true;
    }
  }
  // A trailing comma (`(a, b,)`) leaves us expecting an ident — that's
  // malformed enough to bail. An empty list (`()`) likewise — surface
  // no synthetic columns.
  if (expectingIdent && cols.length > 0) return [];
  return cols;
}

/**
 * Extract the column names declared in a `JSON_TABLE(...)` call's `COLUMNS`
 * clause(s). Scans the function-arg token range for each `COLUMNS ( … )`
 * sub-clause and reads the leading identifier of every top-level column
 * definition (`tag VARCHAR(50) PATH '$'` → `tag`; `id FOR ORDINALITY` → `id`).
 * A `NESTED [PATH] '…' COLUMNS ( … )` definition contributes its inner columns
 * (found when the scan reaches that nested COLUMNS keyword) and is not itself a
 * column. Returns the flattened column-name list.
 */
function parseJsonTableColumns(
  tokens: readonly Token[],
  argsLparenIdx: number,
  argsRparenIdx: number,
): readonly string[] {
  const out: string[] = [];
  for (let i = argsLparenIdx + 1; i < argsRparenIdx; i += 1) {
    const t = tokens[i];
    if (
      (t.type === 'kw' || t.type === 'ident') &&
      t.text.toUpperCase() === 'COLUMNS' &&
      tokens[i + 1] &&
      tokens[i + 1].type === 'lparen'
    ) {
      const close = findMatchingRparen(tokens, i + 1);
      if (close !== -1) readJsonTableColumnDefs(tokens, i + 1, close, out);
      // Don't skip past `close`: a NESTED definition's inner `COLUMNS (…)` sits
      // inside this range and must be visited on its own keyword hit.
    }
  }
  return out;
}

/**
 * Read the leading identifier of each top-level (depth-1) column definition
 * inside one `COLUMNS ( … )` clause. `NESTED` definitions are skipped here
 * (their inner columns are picked up when the outer scan reaches the nested
 * `COLUMNS` keyword).
 */
function readJsonTableColumnDefs(
  tokens: readonly Token[],
  lparenIdx: number,
  rparenIdx: number,
  out: string[],
): void {
  let depth = 0;
  let atDefStart = true;
  for (let i = lparenIdx; i <= rparenIdx; i += 1) {
    const t = tokens[i];
    if (t.type === 'lparen') { depth += 1; continue; }
    if (t.type === 'rparen') { depth -= 1; continue; }
    if (depth !== 1) continue;
    if (t.type === 'comma') { atDefStart = true; continue; }
    if (atDefStart && (t.type === 'ident' || t.type === 'kw')) {
      atDefStart = false;
      if (t.text.toUpperCase() === 'NESTED') continue; // a nesting marker, not a column
      out.push(parseIdentifierToken(t.text).content);
    }
  }
}

/**
 * Extract projected column names from a GRAPH_TABLE(...) call's COLUMNS
 * clause. GRAPH_TABLE columns are `expr [AS] name` (trailing name), unlike
 * JSON_TABLE's leading-name form. `elem.*` entries contribute nothing here —
 * their expansion needs the graph's label properties (snapshot-driven,
 * resolver layer). Returns [] when no COLUMNS clause is found.
 */
function parseGraphTableColumns(
  tokens: readonly Token[], lparenIdx: number, rparenIdx: number,
): readonly string[] {
  // Locate the COLUMNS ( … ) sub-clause inside the call's arg range.
  let colsLparen = -1;
  for (let j = lparenIdx + 1; j < rparenIdx; j += 1) {
    const t = tokens[j];
    if ((t.type === 'kw' || t.type === 'ident') && t.text.toUpperCase() === 'COLUMNS') {
      const open = tokens[j + 1];
      if (open?.type === 'lparen') { colsLparen = j + 1; break; }
    }
  }
  if (colsLparen === -1) return [];
  const colsRparen = findMatchingRparen(tokens, colsLparen);
  if (colsRparen === -1 || colsRparen > rparenIdx) return [];

  // Each top-level comma-separated entry projects the trailing identifier
  // (`expr [AS] name`). Entries ending in `*` (elem.*) contribute nothing —
  // their expansion is snapshot-driven at the resolver layer.
  const cols: string[] = [];
  let depth = 0;
  let lastIdent: string | null = null;
  let endsInStar = false;
  const flush = () => {
    if (lastIdent && !endsInStar) cols.push(lastIdent);
    lastIdent = null;
    endsInStar = false;
  };
  for (let j = colsLparen + 1; j < colsRparen; j += 1) {
    const t = tokens[j];
    if (t.type === 'lparen') { depth += 1; continue; }
    if (t.type === 'rparen') { depth -= 1; continue; }
    if (depth > 0) continue;
    if (t.type === 'comma') { flush(); continue; }
    if (t.type === 'op' && t.text === '*') { endsInStar = true; continue; }
    if (t.type === 'ident' || t.type === 'kw') {
      if (t.text.toUpperCase() === 'AS') continue;
      lastIdent = parseIdentifierToken(t.text).content;
      endsInStar = false;
    }
  }
  flush();
  return cols;
}

export function findMatchingRparen(tokens: readonly Token[], lparenIdx: number): number {
  let depth = 0;
  for (let i = lparenIdx; i < tokens.length; i += 1) {
    if (tokens[i].type === 'lparen') depth += 1;
    else if (tokens[i].type === 'rparen') {
      depth -= 1;
      if (depth === 0) return i;
    }
  }
  return -1;
}

export function isSelectInside(tokens: readonly Token[], lparenIdx: number): boolean {
  for (let i = lparenIdx + 1; i < tokens.length; i += 1) {
    const t = tokens[i];
    if (t.type === 'kw') {
      const kw = t.text.toUpperCase();
      return kw === 'SELECT' || kw === 'WITH' || kw === 'VALUES';
    }
    return false;
  }
  return false;
}
